import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  CheckCircle,
  Mail,
  User,
  GraduationCap,
  BookOpen,
  MessageSquare,
  ArrowRight,
  Home,
  Loader2,
  CreditCard,
  FileText
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LOGO_URL = "https://customer-assets.emergentagent.com/job_academia-sabeneval/artifacts/ai0i1vi4_2F29CCC3-E812-45AF-8948-674464C4314C.PNG";

export default function EnrollmentPage() {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    full_name: "",
    email: "",
    university: "",
    career: "",
    message: ""
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.full_name || !formData.email || !formData.university || !formData.career) {
      toast.error("Por favor completa todos los campos requeridos");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/enrollments`, formData);
      setSubmitted(true);
      toast.success("¡Inscripción enviada exitosamente!");
    } catch (error) {
      console.error("Error submitting enrollment:", error);
      toast.error("Error al enviar la inscripción. Intenta de nuevo.");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50">
        {/* Header */}
        <header className="bg-white border-b border-slate-100 py-4">
          <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
            <a href="/" data-testid="logo-link">
              <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
            </a>
            <Button
              onClick={() => navigate("/")}
              variant="ghost"
              className="text-slate-600"
              data-testid="home-btn"
            >
              <Home className="w-4 h-4 mr-2" />
              Inicio
            </Button>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 py-12">
          <Card className="border-green-100 shadow-lg" data-testid="success-card">
            <CardContent className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>

              <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                ¡Inscripción Recibida!
              </h1>

              <p className="text-slate-600 mb-8">
                Hemos recibido tu solicitud de inscripción. Por favor, sigue los pasos 
                a continuación para completar tu registro.
              </p>

              <div className="bg-purple-50 rounded-xl p-6 text-left mb-8">
                <h3 className="font-semibold text-purple-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5" />
                  Pasos para completar tu inscripción:
                </h3>
                <ol className="space-y-4 text-slate-700">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">1</span>
                    <span>Realiza el pago del curso: <strong>$2,999 MXN</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">2</span>
                    <span>Envía tu comprobante de pago al correo: <strong>sabeneval@gmail.com</strong></span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm flex-shrink-0">3</span>
                    <div>
                      <span>Incluye en tu correo:</span>
                      <ul className="mt-2 ml-4 space-y-1 text-sm text-slate-600">
                        <li>• Nombre completo</li>
                        <li>• Correo electrónico</li>
                        <li>• Carrera que deseas estudiar</li>
                        <li>• Comprobante de pago</li>
                      </ul>
                    </div>
                  </li>
                </ol>
              </div>

              <p className="text-sm text-slate-500 mb-6">
                Una vez verificado tu pago, recibirás acceso al curso en un plazo de 24-48 horas.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate("/")}
                  className="bg-purple-600 hover:bg-purple-700 text-white rounded-full"
                  data-testid="back-home-btn"
                >
                  Volver al Inicio
                </Button>
                <Button
                  onClick={() => navigate("/examen-diagnostico")}
                  variant="outline"
                  className="rounded-full border-purple-200 text-purple-700"
                  data-testid="do-diagnostic-btn"
                >
                  Hacer Diagnóstico
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-100 py-4">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <a href="/" data-testid="logo-link" className="flex items-center gap-2">
            <img src={LOGO_URL} alt="Sabeneval" className="h-10 w-auto" />
            <span className="font-heading font-bold text-lg text-purple-700 hidden sm:block">SABENEVAL</span>
          </a>
          <Button
            onClick={() => navigate("/")}
            variant="ghost"
            className="text-slate-600"
            data-testid="home-btn"
          >
            <Home className="w-4 h-4 mr-2" />
            Inicio
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8 md:py-12">
        <div className="grid md:grid-cols-2 gap-8">
          {/* Payment Instructions */}
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold text-slate-900 mb-4">
              Inscripción al Curso
            </h1>
            <p className="text-slate-600 mb-6">
              Completa el formulario y sigue las instrucciones de pago para acceder al curso.
            </p>

            <Card className="border-purple-100 mb-6" data-testid="price-card">
              <CardContent className="p-6">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500">Precio del curso</p>
                    <p className="text-2xl font-bold text-purple-600">$2,999 MXN</p>
                  </div>
                </div>
                <p className="text-sm text-slate-500">Pago único • Acceso ilimitado</p>
              </CardContent>
            </Card>

            <Card className="border-slate-200" data-testid="instructions-card">
              <CardContent className="p-6">
                <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                  <FileText className="w-5 h-5 text-purple-600" />
                  Instrucciones de Pago
                </h3>
                <ol className="space-y-4 text-sm text-slate-600">
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-slate-100 text-slate-700 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-medium">1</span>
                    <span>Completa el formulario de inscripción</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-slate-100 text-slate-700 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-medium">2</span>
                    <span>Realiza el pago del curso ($2,999 MXN)</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-slate-100 text-slate-700 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-medium">3</span>
                    <div>
                      <span>Envía tu comprobante de pago a:</span>
                      <p className="font-medium text-purple-600 mt-1">sabeneval@gmail.com</p>
                    </div>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-6 h-6 bg-slate-100 text-slate-700 rounded-full flex items-center justify-center text-xs flex-shrink-0 font-medium">4</span>
                    <span>Recibirás acceso al curso en 24-48 horas</span>
                  </li>
                </ol>
              </CardContent>
            </Card>
          </div>

          {/* Enrollment Form */}
          <Card className="border-slate-200 shadow-lg h-fit" data-testid="enrollment-form-card">
            <CardContent className="p-6 md:p-8">
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-6">
                Formulario de Inscripción
              </h2>

              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="space-y-2">
                  <Label htmlFor="full_name" className="flex items-center gap-2">
                    <User className="w-4 h-4 text-slate-400" />
                    Nombre completo *
                  </Label>
                  <Input
                    id="full_name"
                    name="full_name"
                    value={formData.full_name}
                    onChange={handleChange}
                    placeholder="Tu nombre completo"
                    required
                    className="h-11"
                    data-testid="input-full-name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-slate-400" />
                    Correo electrónico *
                  </Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="tu@email.com"
                    required
                    className="h-11"
                    data-testid="input-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="university" className="flex items-center gap-2">
                    <GraduationCap className="w-4 h-4 text-slate-400" />
                    Universidad de interés *
                  </Label>
                  <Input
                    id="university"
                    name="university"
                    value={formData.university}
                    onChange={handleChange}
                    placeholder="Ej: UNAM, IPN, UAM..."
                    required
                    className="h-11"
                    data-testid="input-university"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="career" className="flex items-center gap-2">
                    <BookOpen className="w-4 h-4 text-slate-400" />
                    Carrera deseada *
                  </Label>
                  <Input
                    id="career"
                    name="career"
                    value={formData.career}
                    onChange={handleChange}
                    placeholder="Ej: Medicina, Enfermería, Odontología..."
                    required
                    className="h-11"
                    data-testid="input-career"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message" className="flex items-center gap-2">
                    <MessageSquare className="w-4 h-4 text-slate-400" />
                    Mensaje o duda (opcional)
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="¿Tienes alguna pregunta?"
                    rows={3}
                    className="resize-none"
                    data-testid="input-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={submitting}
                  className="w-full bg-purple-600 hover:bg-purple-700 text-white rounded-full h-12 text-base font-medium"
                  data-testid="submit-enrollment-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                      Enviando...
                    </>
                  ) : (
                    <>
                      Enviar Inscripción
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              </form>

              <p className="text-xs text-slate-500 mt-4 text-center">
                Al enviar este formulario, aceptas recibir información sobre el curso.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
